import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'

const Halifax = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.halifax:About Halifax Paragraph">
        <Paragraph>
          Halifax, the capital of{' '}
          <LinkText href="/rental-listings/nova-scotia">Nova Scotia</LinkText>,
          is known for its vibrant coastal charm and historic significance. With
          a bustling waterfront, rich arts scene, and iconic landmarks like the
          Halifax Citadel, it&apos;s a city that beautifully blends history with
          modernity. Halifax is also home to several universities such as{' '}
          <LinkText href="/rental-listings/city/halifax/dalhousie-university">
            Dalhousie University
          </LinkText>{' '}
          and{' '}
          <LinkText href="/rental-listings/city/halifax/saint-marys-university">
            Saint Mary&apos;s University
          </LinkText>
          , giving it a youthful, energetic vibe. For those looking to settle
          down, there are many{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/5-neighbourhoods-to-find-cheap-rent-in-halifax/">
            neighbourhoods to live in Halifax
          </LinkText>{' '}
          that offer a range of lifestyles, from family-friendly suburbs to
          bustling downtown areas. Its scenic coastline and outdoor activities,
          like exploring Peggy&apos;s Cove, make it a compelling place to call
          home.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <Paragraph>
        <Trans
          i18nKey="seo.city.halifax:Renting FILTER in CITY Paragraph"
          t={t}
        >
          Finding apartments for rent in Halifax involves research,
          communication with{' '}
          <LinkText href="/landlords">landlords & property managers</LinkText>,
          securing references, signing a lease, depositing rent, and learning
          about all the applicable{' '}
          <LinkText href="https://liv.rent/blog/landlords/rental-dictionary-essential-terms-for-renters-and-landlords/">
            rental laws & terminology
          </LinkText>
          . All this can add up to a lengthy endeavour if you’re not familiar
          with how to find an apartment for rent in Halifax.
        </Trans>
      </Paragraph>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,700 - $2,200',
          bed2: '$2,200 - $2,850',
          bed3: '$2,850 - $3,000'
        }}
        rentReportCity={t('cities:city_name_halifax')}
        rentReportLink="https://liv.rent/blog/rent-reports"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.halifax:Apartment Hunting in Halifax Paragraph">
            Finding affordable rentals in Halifax is easier compared to other
            major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>,
            especially if you&apos;re looking for{' '}
            <LinkText href="/rental-listings/city/halifax/under-1000">
              rentals under $1000.
            </LinkText>{' '}
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href="https://liv.rent/blog/renters/how-to-negotiate-rent/">
              how to negotiate rent.
            </LinkText>
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.halifax:Apartment Hunting in LOCATION Paragraph 3">
            Halifax offers a diverse range of neighbourhoods and municipalities
            to choose from when searching for rentals. Depending on your
            preferences, you can explore various rental types, such as{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments.</LinkText> Whether
            you{'`'}re seeking{' '}
            <LinkText href={links.furnished}>furnished</LinkText> or{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>,
            or even considering{' '}
            <LinkText href={links.longTerm}>long term</LinkText> or{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>{' '}
            options, Halifax has a variety of choices to suit your needs.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans
              i18nKey="seo.city.halifax:Apartments for rent LOCATION 2 Paragraph"
              values={{
                location: cityName
              }}
            >
              <LinkText href={links.apartment}>
                Apartments for rent in {{ cityName }}
              </LinkText>{' '}
              are a prevalent housing option, particularly in the urban core,
              appealing to a diverse demographic including students and young
              professionals due to proximity to universities and business areas.
              These apartments often come with modern amenities like fitness
              centers and laundry facilities, enhancing their appeal for those
              seeking convenience. The market offers a range of apartment styles
              and sizes, from studios to three-bedrooms, catering to various
              needs and family sizes.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Houses for rent CITY Paragraph 2">
              Detached{' '}
              <LinkText href={links.house}>
                houses for rent in {{ cityName }}
              </LinkText>{' '}
              offer a sought-after option for families and individuals in search
              of more space and the privacy of a personal yard. The city&apos;s
              rental market features a diverse selection of properties,
              including both partial houses, such as basement apartments and
              duplexes, and entire houses. Renters can choose from a variety of
              older, character-filled homes and modern constructions, each
              located in different neighbourhoods to suit a range of preferences
              and needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Townhouses for rent CITY Paragraph">
              Townhouses are also common in Halifax. They combine the best
              qualities of both apartments and whole houses. Typically less
              expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Basements for rent CITY Paragraph">
              A popular alternative for{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are{' '}
              basements for rent in Halifax . Since rent prices for apartments
              and houses can often get too expensive, many renters seek out
              basements for rent as a cheaper alternative. Homeowners often{' '}
              <LinkText href="https://liv.rent/blog/landlords/how-to-rent-out-your-basement-suite/">
                rent out their basements{' '}
              </LinkText>{' '}
              as a way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Condos for rent Halifax Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Halifax
              </LinkText>{' '}
              are a popular rental option, especially in the downtown area and
              central neighbourhoods where residents value proximity to urban
              amenities and workplaces. These units are ideal for individuals
              and small families who prefer the conveniences of condo living,
              such as built-in maintenance and security services, often
              accompanied by amenities like gyms and pools.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>Rooms for rent in Halifax</LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>
                cat-friendly rental units
              </LinkText>{' '}
              are available throughout the city, as well as properties where
              both are allowed. Keep in mind that some {{ cityName }}{' '}
              neighbourhoods are better suited to those with pets, and adjust
              your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>
                furnished rentals in {{ cityName }}
              </LinkText>
              . Renters seeking furnished units should search for these units
              using filters, but do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Short term rentals CITY Paragraph">
              Halifax, a vibrant city in Nova Scotia, is not Canada&apos;s
              largest city but is known for its diverse job opportunities and
              extensive educational sector, including several universities and
              colleges. It offers a community-oriented lifestyle with a more
              relaxed pace compared to Canada&apos;s larger metropolitan areas.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> are
              fairly common, driven by the city&apos;s steady influx of students
              and seasonal workers, making it an attractive option for those
              seeking flexibility. This is a key factor when{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable.
              </LinkText>{' '}
              For those looking for a quieter experience close to urban
              amenities or temporary lodging during work or study in the region,
              Halifax&apos;s rental options are well-suited to meet a variety of
              needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo.city.halifax:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.halifax:Cheap rentals LOCATION Paragraph 2">
              Compared to other Canadian cities, rent prices in Halifax are
              relatively cheaper. As a result, it shouldn{'`'}t be too hard to
              find something within your budget. If you{'`'}re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Halifax, NS
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for rooms for rent. It{'`'}s worth noting that there are
              neighbourhoods in Halifax where rent is considerably cheaper, so
              exploring{' '}
              <LinkText href={links.cheap}>these affordable options</LinkText>{' '}
              is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo.city.halifax:Living in Halifax Paragraph">
          <Paragraph>
            Living in Halifax offers a unique blend of coastal charm, vibrant
            culture, and a relaxed pace of life. Known for its friendly
            residents and close-knit community feel, Halifax provides a warm,
            welcoming atmosphere. The city’s natural beauty—complete with scenic
            harbors, parks, and beaches—makes outdoor activities a regular part
            of life, from kayaking to coastal hikes. Halifax also has a thriving
            arts and food scene, with diverse local eateries and festivals that
            keep the city lively year-round. With a mix of historic and modern
            neighbourhoods, living in Halifax means enjoying both a rich
            heritage and a comfortable, contemporary lifestyle.
          </Paragraph>
          <Paragraph>
            If you{'`'}re not sure that Halifax is the right city for you, check
            out some of these resources comparing the different cities across
            Canada.
          </Paragraph>
        </Trans>
        {isChinese ? (
          //todo
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E7%B2%BE%E5%93%81%E6%88%BF%E6%BA%90/2020%E7%89%88%E5%A4%A7%E6%B8%A9%E5%93%A5%E5%8D%8E1000%E6%88%96%E4%BB%A5%E4%B8%8B%E5%8F%AF%E4%BB%A5%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%88%BF%EF%BC%9F/">
              $1000加币在温哥华能租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.halifax:Popular Neighbourhoods Halifax Paragraph">
          Like any city, Halifax is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Halifax, South End, North End, West End, Clayton Park, and
          Bedford . For more info about Halifax, you can consult our blog on top{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/5-neighbourhoods-to-find-cheap-rent-in-halifax/">
            neighbourhoods to find rent in Halifax.
          </LinkText>
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans
          i18nKey="seo.city.halifax:Local Landmarks Halifax Paragraph 2"
          t={t}
        >
          Halifax is home to several iconic landmarks that showcase its rich
          history and maritime culture. The{' '}
          <LinkText href="https://www.pc.gc.ca/apps/dfhd/page_nhs_eng.aspx?id=281">
            Halifax Citadel National Historic Site
          </LinkText>{' '}
          is a star-shaped fortress overlooking the city, offering stunning
          views and a glimpse into Halifax`s past as a strategic military base.{' '}
          <LinkText href="https://www.novascotia.com/trip-ideas/stories/perfect-day-halifax-waterfront">
            The Halifax Waterfront
          </LinkText>{' '}
          is a bustling area filled with shops, restaurants, and attractions,
          including the{' '}
          <LinkText href="https://maritimemuseum.novascotia.ca/">
            Maritime Museum of the Atlantic
          </LinkText>
          , which houses exhibits on the Titanic and Nova Scotia’s seafaring
          heritage.{' '}
          <LinkText href="https://www.halifax.ca/parks-recreation/parks-trails-gardens/parks-outdoor-spaces/point-pleasant-park">
            Point Pleasant Park
          </LinkText>{' '}
          offers scenic trails along the coast, while{' '}
          <LinkText href="https://www.novascotia.com/see-do/attractions/peggys-cove-village-and-lighthouse/1468">
            Peggy`s Cove
          </LinkText>
          , a short drive away, features the famous lighthouse and rugged
          coastal scenery, attracting locals and tourists alike.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Halifax
